/**
 * intelligenceRecords.ts — typed wire contracts for the two Intelligence
 * RECORD surfaces: what Phibian recommended, and what it learned.
 *
 * API domains:
 *   `apps/api/src/domains/intelligence/recommendation-record/routes.ts`
 *   `apps/api/src/domains/intelligence/learning/routes.ts`
 *
 * The API package is deliberately NOT imported. Same rule as `intelligence.ts`
 * and `intelligenceThreads.ts`: the admin UI validates and renders these shapes
 * without pulling server code, so the two declarations drift LOUDLY rather than
 * silently.
 *
 * ---------------------------------------------------------------------------
 * WHY THIS MODULE EXISTS, AND WHAT IT IS ALLOWED TO SHOW
 * ---------------------------------------------------------------------------
 * Founder ruling 2 of 2026-09-08 held that a seeded role must not carry
 * permissions for capabilities it cannot reach. `recommendation:read`,
 * `outcome:read` and `learning:read` are seeded to org_admin, supervisor and
 * analyst and had no client at all — no api-client module, no screen, no route.
 * This is the wire half of surfacing them.
 *
 * NOTHING HERE WIDENS THE SERVER. Every route below already refuses on its own
 * terms, and two of the three keys are not door keys at all:
 *
 *   `recommendation:read`  the door on both recommendation reads.
 *   `outcome:read`         checked PER RECORD by the outcome admission gate,
 *                          inside the detail read. A caller without it gets a
 *                          detail whose `outcome.state` is `withheld`, not a
 *                          403 — which is why the screen must render that state
 *                          and never collapse it into "none".
 *   `learning:read`        the door on both learning reads; each signal is then
 *                          checked against its own inherited reach.
 *
 * ---------------------------------------------------------------------------
 * COUNTS THAT ARE NEVER IDENTIFIERS
 * ---------------------------------------------------------------------------
 * `withheld`, `unreconciled`, `scanned`, `rowCount`, `parameterCount` and every
 * field of `confidence.observed` / `confidence.outcome` are COUNTS by design.
 * The server refuses to disaggregate them because doing so rebuilds the oracle
 * these routes exist to prevent. A caller must not try to expand one, and a
 * screen must not imply a reason for one.
 *
 * `withheld` is also the ONLY thing that separates "there is little advice"
 * from "there is advice you may not see". A renderer that drops it turns a
 * withholding into an absence, which is the collapse this codebase names as its
 * most common defect.
 *
 * ---------------------------------------------------------------------------
 * CASING IS INCONSISTENT ACROSS ONE RESPONSE, ON PURPOSE
 * ---------------------------------------------------------------------------
 * On the detail read, `recommendation`, `doctrine`, `feedback` and
 * `confirmation` are the durable rows sent through unchanged (snake_case);
 * `evidence`, `confidence` and `outcome` are server-built projections
 * (camelCase). This module mirrors the wire rather than normalising it, so that
 * a field renamed on either side fails here instead of being quietly mapped.
 */
import { z } from 'zod';
import { get } from './transport.js';
// ---------------------------------------------------------------------------
// Bounds and vocabularies — mirrored from the server's own constants.
// ---------------------------------------------------------------------------
/** `listQuerySchema` on both list routes: 1..200, default 50. */
export const RECORD_LIST_MAX_LIMIT = 200;
/**
 * `FEEDBACK_SIGNALS` (recommendation-record/repository.ts), in the server's
 * order. Twelve signals across five axes.
 */
export const FEEDBACK_SIGNALS = [
    'accepted',
    'rejected',
    'modified',
    'partially_accepted',
    'action_executed',
    'action_failed',
    'outcome_improved',
    'outcome_worsened',
    'false_positive_anomaly',
    'confirmed_anomaly',
    'user_correction',
    'preferred_alternative',
];
/**
 * `OUTCOME_AXIS_SIGNALS` (outcomes/deriveFromFeedback.ts). EXACTLY these two
 * derive an outcome record; the other ten do not, and a screen that presents
 * all twelve as one list is asking an operator to file permanent outcome memory
 * without saying so.
 */
export const OUTCOME_AXIS_SIGNALS = ['outcome_improved', 'outcome_worsened'];
export const isOutcomeAxis = (signal) => OUTCOME_AXIS_SIGNALS.includes(signal);
/**
 * The outcome state. SIX values, not five — `unreconciled` arrived with founder
 * ruling 3 of 2026-09-07 and the server's own prose still says five, so this
 * list is taken from the TYPE and not from the sentence beside it.
 *
 *   measured          something was filed and this caller may read it
 *   not_yet_measured  nothing was ever filed. NOT a zero and NOT a failure
 *   not_applicable    the record declared no feasible action
 *   withheld          it exists and this caller may not read it
 *   unreconciled      its reach was never computed, so nobody can say who may
 *   unavailable       the permission catalog could not be resolved
 */
export const OUTCOME_STATES = [
    'measured',
    'not_yet_measured',
    'not_applicable',
    'withheld',
    'unreconciled',
    'unavailable',
];
/** `EVIDENCE_STRENGTHS` (recommendation-record/confidence.ts), weakest first. */
export const EVIDENCE_STRENGTHS = ['insufficient', 'single_source', 'corroborated'];
/** `LEARNING_SIGNAL_KINDS` / `_DIRECTIONS` (intelligence/learning/types.ts). */
export const LEARNING_SIGNAL_KINDS = ['adoption', 'friction', 'efficacy', 'safety'];
export const LEARNING_SIGNAL_DIRECTIONS = ['increase', 'decrease', 'stable'];
// ---------------------------------------------------------------------------
// Recommendations
// ---------------------------------------------------------------------------
/**
 * `RECOMMENDATION_COLUMNS` (recommendation-record/repository.ts), sent through
 * unchanged. `request_id` is written and never selected, so it is not here.
 *
 * `created_at` is a `timestamptz`. node-postgres installs no type parser for
 * OID 1184, so it arrives as a `Date` and JSON-serialises to an ISO string —
 * hence `z.string()` on a column the server's own row interface calls a string.
 */
export const recommendationSchema = z
    .object({
    id: z.string().uuid(),
    org_id: z.string().uuid(),
    requested_by: z.string().uuid().nullable(),
    framework_key: z.string(),
    framework_version: z.number().int(),
    subject: z.string(),
    question: z.string(),
    headline: z.string(),
    tradeoffs: z.string().nullable(),
    risk: z.string(),
    advisory_only: z.boolean(),
    no_feasible_action: z.boolean(),
    confirmation_required: z.boolean(),
    /** A COUNT. Equal-standing contradictions the frame could not decide. */
    unresolved_conflict_count: z.number().int(),
    training_use: z.string(),
    created_at: z.string(),
})
    .strict();
/**
 * One retrieval, as the route projects it — the MANIFEST, never the retrieved
 * content and never the recorded call parameters. `parameterCount` is the
 * deliberate marker of that omission: it makes the absence visible instead of
 * silent, and there is no route that expands it.
 */
export const evidenceSchema = z
    .object({
    id: z.string().uuid(),
    ordinal: z.number().int(),
    toolName: z.string(),
    scopePermission: z.string(),
    scopeUserId: z.string().uuid().nullable(),
    rowCount: z.number().int(),
    contentHash: z.string(),
    retrievedAt: z.string(),
    parameterCount: z.number().int(),
})
    .strict();
/**
 * How much the manifest actually supports, computed from the stored record so
 * the headline and its honesty about itself cannot be obtained separately.
 *
 * `outcome.consulted` is the field that distinguishes a band computed after
 * looking at the outcome store from one computed without it. A screen that
 * renders the band without it is stating a confidence whose basis it does not
 * know.
 */
export const confidenceSchema = z
    .object({
    strength: z.enum(EVIDENCE_STRENGTHS),
    basis: z.array(z.string()),
    limits: z.array(z.string()),
    caveat: z.string(),
    observed: z
        .object({
        retrievals: z.number().int(),
        distinctTools: z.number().int(),
        rowsRetrieved: z.number().int(),
        emptyRetrievals: z.number().int(),
        unresolvedConflicts: z.number().int(),
    })
        .strict(),
    outcome: z
        .object({
        measured: z.number().int(),
        worsened: z.number().int(),
        improved: z.number().int(),
        withheld: z.number().int(),
        unreconciled: z.number().int(),
        consulted: z.boolean(),
    })
        .strict(),
})
    .strict();
/**
 * One doctrine rule the advice was composed under, as the snapshot recorded it.
 * `requirement_value` is a numeric column and node-postgres hands numerics back
 * as STRINGS; it is kept a string here rather than coerced, because a value
 * whose precision the client invented is worse than one it did not read.
 */
export const doctrineSnapshotSchema = z
    .object({
    id: z.string().uuid(),
    position: z.number().int(),
    rule_scope: z.string(),
    rule_id: z.string().uuid(),
    rule_version: z.number().int().nullable(),
    rule_version_label: z.string().nullable(),
    authority: z.string(),
    bindingness: z.string(),
    subject: z.string(),
    requirement_kind: z.string(),
    requirement_value: z.string().nullable(),
    requirement_unit: z.string().nullable(),
    as_of_date: z.string(),
    rule_effective_from: z.string().nullable(),
    rule_effective_to: z.string().nullable(),
    rule_role: z.string(),
    disposition: z.string(),
    conflict_reason: z.string().nullable(),
    displaced_by_scope: z.string().nullable(),
    displaced_by_rule_id: z.string().uuid().nullable(),
})
    .strict();
/** A recorded signal. `signal_axis` is a GENERATED column, so it is the server's answer. */
export const feedbackSchema = z
    .object({
    id: z.string().uuid(),
    signal: z.string(),
    signal_axis: z.string(),
    observed_at: z.string().nullable(),
    recorded_at: z.string(),
})
    .strict();
/** `null` means NOTHING WAS APPROVED, which is a recommendation's normal state. */
export const confirmationSchema = z
    .object({
    id: z.string().uuid(),
    recommendation_id: z.string().uuid(),
    confirmed_by: z.string().uuid(),
    confirmed_option_id: z.string().uuid().nullable(),
    shown_change_digest: z.string(),
    confirmed_at: z.string(),
})
    .strict();
/**
 * What happened after the advice, and how sure the answer is that it knows.
 *
 * `outcomes` is EMPTY for every state but `measured`, and the four counts are
 * counts. `signal` and `source` are open strings rather than enums: the store
 * holds payload kinds from more than one writer, and an unknown kind must be
 * COUNTED and shown, never guessed at or dropped.
 */
export const outcomeStatusSchema = z
    .object({
    state: z.enum(OUTCOME_STATES),
    outcomes: z.array(z
        .object({
        outcomeRecordId: z.string(),
        signal: z.string(),
        /** Milliseconds since epoch. A NUMBER on this route, unlike every other instant. */
        observedAtMs: z.number(),
        source: z.string(),
        explicit: z.boolean(),
    })
        .strict()),
    /** Exists and is out of reach. A grant would resolve it. */
    withheld: z.number().int(),
    /** Reach never computed. NO grant resolves it; provenance does. Never summed into withheld. */
    unreconciled: z.number().int(),
    scanned: z.number().int(),
})
    .strict();
export const recommendationListSchema = z
    .object({
    recommendations: z.array(recommendationSchema),
    withheld: z.number().int(),
    scanned: z.number().int(),
    windowExhausted: z.boolean(),
})
    .strict();
export const recommendationDetailSchema = z
    .object({
    recommendation: recommendationSchema,
    doctrine: z.array(doctrineSnapshotSchema),
    evidence: z.array(evidenceSchema),
    confidence: confidenceSchema,
    feedback: z.array(feedbackSchema),
    confirmation: confirmationSchema.nullable(),
    outcome: outcomeStatusSchema,
})
    .strict();
/**
 * The response to a filed signal. 201 EVEN WHEN THE DERIVED HALF IS REFUSED —
 * "your signal was recorded and you may not file outcome memory" is a correct
 * configured answer, not an error, and folding it into a 403 would make callers
 * believe the signal was lost. `reason` is present only on `refused`;
 * `outcomeRecordId` only on `admitted`.
 */
export const feedbackResultSchema = z
    .object({
    feedback: feedbackSchema,
    outcome: z
        .object({
        derivation: z.enum(['admitted', 'not_outcome_axis', 'refused']),
        outcomeRecordId: z.string().optional(),
        reason: z.string().optional(),
    })
        .strict(),
})
    .strict();
export function listRecommendations(params = {}, options) {
    return get('/api/intelligence/recommendations', {
        ...options,
        query: { ...(params.limit !== undefined ? { limit: params.limit } : {}) },
    }).then((response) => recommendationListSchema.parse(response));
}
export function getRecommendation(recommendationId, options) {
    return get(`/api/intelligence/recommendations/${encodeURIComponent(recommendationId)}`, options).then((response) => recommendationDetailSchema.parse(response));
}
/**
 * THERE IS DELIBERATELY NO `recordFeedback` BINDING HERE, and this is the note
 * that says why rather than leaving the absence to look like an oversight.
 *
 * `POST .../feedback` exists and is guarded on `recommendation:feedback`, which
 * migration 2018000000000 removed from the seeded `analyst` role — the persona
 * this module was written to serve. On the outcome axis that write derives
 * PERMANENT outcome memory, so its control belongs on a screen built for the
 * operator who received the advice, with the two outcome-axis signals separated
 * from the other ten. Shipping the binding ahead of that screen would put a
 * typed, unreachable write in the client, which is the exact shape
 * `client-binding-callers.test.ts` exists to refuse: it makes the capability
 * look exposed to the next reader and to the capability ledger, and nothing can
 * call it.
 *
 * The response CONTRACT is kept above (`feedbackResultSchema`, `FEEDBACK_SIGNALS`,
 * `OUTCOME_AXIS_SIGNALS`) because a screen that adds the control needs to know
 * that a refused derivation still arrives as a 201 — and that is a fact about
 * the route, not a request this module makes.
 */
// ---------------------------------------------------------------------------
// Learning signals
// ---------------------------------------------------------------------------
/**
 * A signal, as `projectSignal` sends it. Deliberately NOT the whole stored
 * record: `orgId`, `expiration` and `revocationState` are stripped at the wire.
 *
 * `permissionRequirements` is the signal's own audience — the union of the
 * permissions of every outcome record it cites — which is why `learning:read`
 * is permission to ASK and furnishes no room by itself.
 */
export const learningSignalSchema = z
    .object({
    id: z.string(),
    kind: z.enum(LEARNING_SIGNAL_KINDS),
    direction: z.enum(LEARNING_SIGNAL_DIRECTIONS),
    confidence: z.number(),
    period: z.object({ start: z.string(), end: z.string() }).strict(),
    derivedFrom: z
        .object({ outcomeRefs: z.array(z.string()), telemetryRefs: z.array(z.string()) })
        .strict(),
    evidenceRefs: z.array(z.string()),
    retentionClass: z.string(),
    sensitivityClassification: z.string(),
    permissionRequirements: z.array(z.string()),
    contentHash: z.string(),
    payload: z
        .object({ labels: z.array(z.string()).optional(), note: z.string().optional() })
        .strict()
        .optional(),
})
    .strict();
/**
 * `catalogLoaded: false` arrives with a 200 and all-zero counts, and it is a
 * REFUSAL THAT SAYS SO rather than a measurement. A screen that renders it as
 * "no learning yet" reports an outage as an organizational fact.
 */
export const learningSignalListSchema = z
    .object({
    signals: z.array(learningSignalSchema),
    withheld: z.number().int(),
    scanned: z.number().int(),
    catalogLoaded: z.boolean(),
    windowExhausted: z.boolean(),
})
    .strict();
export function listLearningSignals(params = {}, options) {
    return get('/api/intelligence/learning/signals', {
        ...options,
        query: { ...(params.limit !== undefined ? { limit: params.limit } : {}) },
    }).then((response) => learningSignalListSchema.parse(response));
}
/**
 * NO SINGLE-SIGNAL BINDING EITHER, for the same reason and one of its own.
 *
 * `GET .../signals/:signalId` exists, but absent, revoked, expired and
 * out-of-reach all collapse into ONE 404 with one body — deliberately, so the
 * id is not an existence oracle. A detail screen therefore cannot tell a reader
 * which of those four happened, and everything the list already renders about a
 * signal (kind, direction, period, inherited reach, how many outcome records it
 * cites) is everything the projection carries. There is nothing a detail would
 * add and something it would have to lie about, so no screen calls it and no
 * binding is shipped for it.
 */
//# sourceMappingURL=intelligenceRecords.js.map